// H-18: the transcript.
//
// Everything the candidate and Elliott said, in order. Three things it has to
// make plain, and all three are compliance requirements rather than niceties:
//
//   Which turns were ATTEMPTS and which were questions. A question does not use
//   an attempt (PRD 7.1), and a reviewer who cannot tell will read three
//   exchanges as three failed tries.
//
//   That a spoken answer is shown as the TRANSCRIPT the candidate checked and
//   could correct before sending (C-5b). What was graded is these words, never
//   the recording, which no longer exists (L-4).
//
//   That the response time and the input mode are CONTEXT, NOT EVIDENCE (L-6).
//   The caption comes from the server with the data, so it cannot be dropped by
//   a layout change here.

import React from 'react';
import { Box, Typography } from '@mui/material';
import { Bone, fadeInSx } from '../../../../components/ui/DashboardSkeletons';
import { useHiringTranscript } from '../../../../queries/useHiring';
import { BODY, BRANDY, FONT, LABEL, LINE, TEXT, TEXT_2, TEXT_3, WELL } from '../capability-setup/designSystem';
import QuizCard from '../home/elliott-lesson/components/QuizCard';
import { GRID_LINE, IntegrityCaption, seconds, signals } from './hiringParts';

const BRANDY_RULE = '1px solid rgba(247, 148, 32, 0.45)';
const LINE_DIVIDE = { width: '1px', height: 14, flexShrink: 0, bgcolor: '#3A3A3A' };

/** One turn: who said it, then what is known about how, on the right, then the words. */
const Turn: React.FC<{ from: 'candidate' | 'elliott'; facts?: string[]; children: string }> = ({
  from,
  facts = [],
  children
}) => (
  <Box
    sx={{
      p: '10px 12px',
      borderRadius: '4px',
      border: `1px solid ${LINE}`,
      display: 'flex',
      flexDirection: 'column',
      gap: '6px'
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
      <Typography sx={{ ...LABEL, color: from === 'candidate' ? TEXT_2 : BRANDY, flex: 1, minWidth: 'fit-content' }}>
        {from === 'candidate' ? 'Candidate' : 'Elliott'}
      </Typography>
      {facts.map((fact, index) => (
        <React.Fragment key={fact}>
          {index > 0 && <Box sx={LINE_DIVIDE} />}
          <Typography sx={{ ...BODY, fontSize: 12, color: TEXT_3 }}>{fact}</Typography>
        </React.Fragment>
      ))}
    </Box>
    <Typography sx={{ ...BODY, color: TEXT, whiteSpace: 'pre-wrap' }}>{children}</Typography>
  </Box>
);

const TranscriptPanel: React.FC<{ candidateId: string }> = ({ candidateId }) => {
  const transcript = useHiringTranscript(candidateId, true);
  if (transcript.isLoading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px', ...fadeInSx }}>
        <Bone height={120} />
        <Bone height={120} />
      </Box>
    );
  }
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
      <IntegrityCaption caption={transcript.data?.integrityCaption} />
      {(transcript.data?.capabilities || []).map((capability) => (
        <Box
          key={capability.capabilityId}
          sx={{
            p: '14px 16px',
            borderRadius: '8px',
            bgcolor: WELL,
            border: BRANDY_RULE,
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            '& > * + * + *': { borderTop: GRID_LINE, pt: '12px' }
          }}
        >
          <Typography
            sx={{ fontFamily: FONT, fontSize: 14, fontWeight: 600, color: BRANDY, pb: '10px', borderBottom: BRANDY_RULE }}
          >
            {capability.capabilityName}
          </Typography>

          {/* The situation is what Elliott opened with, so it reads as the first turn. */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <Turn from="elliott">{capability.situation}</Turn>
            {capability.exchanges.map((exchange, index) => (
              <Turn
                key={index}
                from={exchange.from}
                facts={
                  exchange.from === 'candidate'
                    ? [
                        ...(exchange.clarification ? ["A question, so it didn't use an attempt"] : []),
                        ...(exchange.inputMode
                          ? [exchange.inputMode === 'voice' ? 'Voice' : 'Typed']
                          : []),
                        ...(exchange.responseSeconds !== null ? [`Replied in ${seconds(exchange.responseSeconds)}`] : []),
                        ...signals(exchange)
                      ]
                    : []
                }
              >
                {exchange.text}
              </Turn>
            ))}
            {capability.exchanges.length === 0 && (
              <Typography sx={{ ...BODY, fontSize: 12.5, color: TEXT_3 }}>Not answered yet.</Typography>
            )}
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <Typography sx={{ ...LABEL, color: TEXT }}>Quiz Question</Typography>
            {capability.quiz.answerIndex === null ? (
              <>
                <Typography sx={{ ...BODY, color: TEXT_2 }}>{capability.quiz.question}</Typography>
                <Typography sx={{ ...BODY, fontSize: 12.5, color: TEXT_3 }}>Not answered.</Typography>
              </>
            ) : (
              // The question as the candidate's own lesson shows it, read back afterwards.
              <QuizCard
                instant
                compact
                revealCorrect
                question={capability.quiz.question}
                options={capability.quiz.options.map((label, index) => ({ id: String(index), label }))}
                selectedId={String(capability.quiz.answerIndex)}
                result={{
                  selectedCorrect: capability.quiz.answerIndex === capability.quiz.correct,
                  correctOptionId: String(capability.quiz.correct),
                  explanation: ''
                }}
                onSelect={() => undefined}
              />
            )}
          </Box>
        </Box>
      ))}
    </Box>
  );
};

export default TranscriptPanel;
